import { GiftOutlined } from "@ant-design/icons";
import { Button, Modal, Skeleton } from "antd";
import {
	lazy,
	ReactNode,
	Suspense,
	useContext,
	useMemo,
	useState,
} from "react";
import { Link, useSearchParams } from "react-router-dom";
import HeroIllustration from "../../assets/image/help-centre-hero.svg?react";
import ConfigureLink from "../../components/HelpCentre/ConfigureLink";
import FaqList from "../../components/HelpCentre/FaqList";
import HelpComingSoon from "../../components/HelpCentre/HelpComingSoon";
import HelpPanel from "../../components/HelpCentre/HelpPanel";
import HelpSearchBox from "../../components/HelpCentre/HelpSearchBox";
import HelpState from "../../components/HelpCentre/HelpState";
import SearchResults from "../../components/HelpCentre/SearchResults";
import TopicGrid from "../../components/HelpCentre/TopicGrid";
import { HomeContext } from "../../containers/Home";
import {
	useHelpDefaultFaq,
	useHelpSearchIndex,
	useReaderTopics,
	useSearchQueryParam,
} from "../../hooks/useHelpCentre";
import {
	FAQ_PARAM,
	helpConfigurationPath,
	helpTopicsPath,
} from "../../utils/helpCentre/content";
import { queryTerms, searchHelp } from "../../utils/helpCentre/search";

// The editor for the FAQs below, loaded only when an editor opens it.
const DefaultFaqEditorModal = lazy(
	() => import("../../components/HelpCentre/editor/DefaultFaqEditorModal")
);

/** Topic cards on the landing page; "View all" lists the rest (as Engage). */
const LANDING_TOPIC_COUNT = 6;

const DEFAULT_FAQ_TITLE = "Frequently asked questions";

const LOADING_LINES = <Skeleton active title={false} paragraph={{ rows: 4 }} />;

const resultCount = (count: number) =>
	`${count} ${count === 1 ? "result" : "results"}`;

/**
 * The Help Centre's landing page: a hero with the search, the first topics
 * (or, while searching, the results) and the default FAQs. Each panel follows
 * its own query, so one failing to load doesn't stop the other.
 */
function HelpCentreHome() {
	const { setWhatsNewOpen } = useContext(HomeContext) as {
		setWhatsNewOpen?: (open: boolean) => void;
	};
	const [searchParams] = useSearchParams();
	const openFaqKey = searchParams.get(FAQ_PARAM);
	const [isFaqEditorOpen, setFaqEditorOpen] = useState(false);

	const topics = useReaderTopics();
	const faqs = useHelpDefaultFaq();
	// An empty title shows the default one.
	const faqTitle = faqs.defaultFaq?.title ?? "";
	const index = useHelpSearchIndex(topics.topics, faqs.defaultFaq);
	const { input, setInput, query } = useSearchQueryParam();
	const terms = useMemo(() => queryTerms(query), [query]);
	const results = useMemo(() => searchHelp(index, terms), [index, terms]);
	const isSearching = terms.length > 0;

	let topicsContent: ReactNode;
	if (topics.isError) {
		topicsContent = (
			<HelpState
				onRetry={() => topics.refetch()}
				retrying={topics.isFetching}
				message="We couldn't load the topics."
			/>
		);
	} else if (!isSearching) {
		topicsContent = (
			<TopicGrid
				topics={topics.topics?.slice(0, LANDING_TOPIC_COUNT)}
				empty={<HelpComingSoon />}
			/>
		);
	} else if (!topics.topics) {
		topicsContent = LOADING_LINES;
	} else {
		topicsContent = (
			<SearchResults results={results} terms={terms} query={query} />
		);
	}

	let faqContent: ReactNode;
	if (faqs.isError) {
		faqContent = (
			<HelpState
				onRetry={() => faqs.refetch()}
				retrying={faqs.isFetching}
				message="We couldn't load the frequently asked questions."
			/>
		);
	} else if (!faqs.defaultFaq) {
		faqContent = LOADING_LINES;
	} else {
		faqContent = (
			<FaqList
				items={faqs.defaultFaq.faq}
				variant="plain"
				openKey={openFaqKey}
				emptyText="There are no frequently asked questions yet."
			/>
		);
	}

	return (
		<>
			<div className="help-hero">
				<div className="help-hero__inner">
					<div className="help-hero__text">
						<h1 className="help-hero__title">Help Centre</h1>
						<p className="help-hero__subtitle">
							Find the answers and resources you need
						</p>
						<HelpSearchBox
							size="hero"
							value={input}
							onChange={setInput}
							label="Search the Help Centre"
							placeholder="Search guides, FAQs and videos"
						/>
						<Button
							type="link"
							className="help-hero__whats-new"
							icon={<GiftOutlined aria-hidden="true" />}
							onClick={() => setWhatsNewOpen?.(true)}
						>
							What&apos;s new in Terra
						</Button>
					</div>
					<HeroIllustration
						className="help-hero__illustration"
						aria-hidden="true"
					/>
				</div>
			</div>

			<div className="help-body">
				{isSearching ? (
					<HelpPanel
						title="Search results"
						actions={
							topics.topics && (
								<span className="help-panel__count">
									{resultCount(results.length)}
								</span>
							)
						}
					>
						{topicsContent}
					</HelpPanel>
				) : (
					<HelpPanel
						title="Topics"
						actions={
							<>
								<Link className="help-panel__link" to={helpTopicsPath()}>
									View all
								</Link>
								<ConfigureLink
									label="Configure topics"
									to={helpConfigurationPath()}
								/>
							</>
						}
					>
						{topicsContent}
					</HelpPanel>
				)}

				<HelpPanel
					title={faqTitle || DEFAULT_FAQ_TITLE}
					actions={
						<ConfigureLink
							label="Edit the frequently asked questions"
							onClick={() => setFaqEditorOpen(true)}
						/>
					}
				>
					{faqContent}
				</HelpPanel>
			</div>

			<output className="sr-only">
				{isSearching && topics.topics
					? `${resultCount(results.length)} for ${query.trim()}`
					: ""}
			</output>

			{isFaqEditorOpen && (
				<Suspense
					fallback={
						<Modal
							open
							loading
							footer={null}
							title="Frequently asked questions"
							onCancel={() => setFaqEditorOpen(false)}
						/>
					}
				>
					<DefaultFaqEditorModal onClosed={() => setFaqEditorOpen(false)} />
				</Suspense>
			)}
		</>
	);
}

export default HelpCentreHome;
